import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { currentCountry, instanceName } from "@/jurisdictions/country";

const AuthContext = createContext(null);

function applyBranding(cabinet) {
  // The platform palette follows the Ninyaa logo (emerald). We intentionally
  // do NOT override --primary per cabinet so the brand theme stays consistent.
  void cabinet;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon
  const [cabinet, setCabinet] = useState(null);
  const [ready, setReady] = useState(false);
  const [instanceError, setInstanceError] = useState("");

  const loadCabinet = useCallback(async () => {
    try {
      const { data } = await api.get("/cabinet");
      setCabinet(data);
      applyBranding(data);
      return data;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    let active = true;
    const initialize = async () => {
      try {
        const { data } = await api.get("/instance");
        if (data.country !== currentCountry()) throw new Error("L’interface et le serveur doivent être configurés pour la même instance.");
        if (!active) return;
        document.title = instanceName();
      } catch (error) {
        if (active) setInstanceError(error.response?.data?.detail || "Impossible de vérifier l’identité du serveur. Vérifiez que les services locaux sont démarrés.");
        return;
      }
      if (localStorage.getItem("fidalis_token")) {
        try {
          const { data } = await api.get("/auth/me");
          if (data.instance_country !== currentCountry()) throw new Error("Instance incompatible");
          if (!active) return;
          if (currentCountry() !== "FR" || data.role !== "client") await loadCabinet();
          else setCabinet(null);
          setUser(data);
        } catch (error) {
          if ([401, 403].includes(error.response?.status)) localStorage.removeItem("fidalis_token");
          if (active) setUser(false);
        }
      } else if (active) setUser(false);
      if (active) setReady(true);
    };
    initialize();
    return () => { active = false; };
  }, [loadCabinet]);

  const userId = user?.id;
  const userRole = user?.role;
  useEffect(() => {
    if (!ready || !userId) return;
    const controller = new AbortController();
    let pending = false;
    const refreshAccess = async () => {
      const token = localStorage.getItem("fidalis_token");
      if (document.visibilityState !== "visible" || pending || !token) return;
      pending = true;
      try {
        const [profile, workspace] = await Promise.all([
          api.get("/auth/me", { signal: controller.signal }),
          currentCountry() === "FR" && userRole === "client" ? Promise.resolve({ data: null }) : api.get("/cabinet", { signal: controller.signal }),
        ]);
        if (controller.signal.aborted || token !== localStorage.getItem("fidalis_token")) return;
        if (profile.data.instance_country !== currentCountry()) return;
        setUser(profile.data);
        setCabinet(workspace.data);
      } catch (error) {
        if (!controller.signal.aborted && token === localStorage.getItem("fidalis_token") && [401, 403].includes(error.response?.status)) {
          localStorage.removeItem("fidalis_token");
          setUser(false);
          setCabinet(null);
        }
      } finally { pending = false; }
    };
    window.addEventListener("focus", refreshAccess);
    document.addEventListener("visibilitychange", refreshAccess);
    return () => {
      controller.abort();
      window.removeEventListener("focus", refreshAccess);
      document.removeEventListener("visibilitychange", refreshAccess);
    };
  }, [ready, userId, userRole]);

  const login = async (token, u) => {
    if (u.instance_country !== currentCountry()) throw new Error("Ce compte n’est pas autorisé sur cette instance.");
    localStorage.setItem("fidalis_token", token);
    if (currentCountry() !== "FR" || u.role !== "client") await loadCabinet();
    else setCabinet(null);
    setUser(u);
  };

  const logout = useCallback(() => {
    const token = localStorage.getItem("fidalis_token");
    if (token) api.post("/auth/logout", {}, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    localStorage.removeItem("fidalis_token");
    localStorage.removeItem("fidalis_last_gl");
    setUser(false);
    setCabinet(null);
  }, []);

  const markTourSeen = useCallback(async () => {
    setUser((u) => (u ? { ...u, tour_seen: true } : u));
    try { await api.post("/auth/tour-complete"); } catch { /* ignore */ }
  }, []);

  return (
    <AuthContext.Provider value={{ user, cabinet, ready, instanceError, login, logout, refreshCabinet: loadCabinet, markTourSeen }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
